import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { registrationActions } from 'careand-redux/slices/registration';
import { RegistrationBottomNavigation } from 'scenes/PatientRegistration/Navigation';
import DefaultPatientTextField from 'components/Utils/DefaultPatientTextField';
import { registrationCredentialsSchema, RegistrationCredentialsValues } from 'types/forms';

const Credentials = ({
  track,
  onSuccess,
}: {
  track: (event: string, data?: any) => void;
  onSuccess: () => void;
}) => {
  const dispatch = useAppDispatch();
  const credentials = useAppSelector((state) => state.registration.credentials);
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<RegistrationCredentialsValues>({
    resolver: zodResolver(registrationCredentialsSchema),
    defaultValues: {
      email: '',
      username: '',
      password: '',
      password2: '',
      terms: false as any,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    track('registrationCredentials');
    if (credentials) {
      reset({ ...credentials, terms: false as any });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track]);

  async function onFormSubmitted(values: RegistrationCredentialsValues) {
    setLoading(true);
    const { isSuccess } = await dispatch(
      api.Authentication.checkAvailableUsername({ username: values.username, role: 'patient' }),
      { errorTitle: 'Account Credentials', meta: true },
    );
    setLoading(false);

    if (isSuccess) {
      dispatch(registrationActions.saveRegistrationCredentials(values));
      track('registrationCredentials', { email: values.email });
      onSuccess();
    }
  }

  return (
    <div className="credentials step">
      <span className="top-text small">This will take a minute... Literally!</span>
      <form className="credentials-form" onSubmit={handleSubmit(onFormSubmitted)}>
        <DefaultPatientTextField name="email" control={control} label="Email address" />
        <DefaultPatientTextField name="username" control={control} label="Username" />
        <DefaultPatientTextField name="password" control={control} label="Password" type="password" />
        <DefaultPatientTextField
          name="password2"
          control={control}
          label="Confirm Password"
          type="password"
        />

        <div className="terms-conditions">
          <Controller
            name="terms"
            control={control}
            render={({ field, fieldState }) => (
              <div
                id="terms-conditions-warner"
                style={{
                  borderBottom: fieldState.isTouched && fieldState.error ? '2px solid red' : 'none',
                }}
              >
                <input
                  checked={field.value === true}
                  id="terms-and-conditions"
                  type="checkbox"
                  onChange={(e) => field.onChange(e.target.checked)}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
                <span>I agree to the </span>
                <a
                  className="open-terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://careand.ca/terms-of-use"
                >
                  Terms & Conditions
                </a>
              </div>
            )}
          />
        </div>

        <RegistrationBottomNavigation
          id="submit-credentials"
          disabled={!isValid}
          loading={loading}
        />
      </form>
    </div>
  );
};

export default Credentials;
